/**
 * Phone normalisation for the tenant's country.
 *
 * Why: leads and customers are matched by the STORED `phone_last9` column
 * (last nine digits). That is stable for AU, US, CA and UK numbers whether
 * typed nationally or internationally, but a 9-digit NZ number typed as
 * "09 312 3456" and delivered by call tracking as "+6493123456" give different
 * last-nine values. Storing every number in E.164 removes the ambiguity for
 * every market, so forms and inbound webhooks call `normalizePhoneE164`
 * before saving. Mirrors `normalisePhoneE164` in supabase/functions/_shared/square.ts.
 */
import { resolveCompanyLocale, type CompanyLocale } from './locale';

/**
 * Convert a phone number as typed into E.164 for the given country.
 * Returns the input trimmed when it cannot be normalised (too short/long),
 * so nothing is ever thrown away.
 */
export function normalizePhoneE164(raw: string | null | undefined, locale: CompanyLocale | string | null | undefined): string {
  const input = (raw ?? '').trim();
  if (!input) return '';
  const loc: CompanyLocale =
    typeof locale === 'string' ? resolveCompanyLocale(locale) : locale ?? resolveCompanyLocale(undefined);

  const hasPlus = input.startsWith('+');
  let digits = input.replace(/\D/g, '');
  if (!digits) return input;

  // "+61 4..." or "0061 4..." already carry the country code.
  if (hasPlus) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : input;
  if (digits.startsWith('00') && digits.length > 10) {
    digits = digits.slice(2);
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : input;
  }

  // North America: a bare 10-digit number, or 11 digits starting with 1.
  if (loc.dialCode === '1') {
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    if (digits.length === 10) return `+1${digits}`;
    return input;
  }

  // Everyone else: national trunk prefix "0" is dropped, dial code prepended.
  if (digits.startsWith(loc.dialCode) && digits.length >= loc.dialCode.length + 8 && digits.length <= 15) {
    return `+${digits}`;
  }
  const national = digits.replace(/^0/, '');
  if (national.length >= 7 && national.length <= 12) return `+${loc.dialCode}${national}`;
  return input;
}

/** Last nine digits — the same expression the DB `phone_last9` columns use. */
export function phoneLast9(raw: string | null | undefined): string | null {
  const digits = (raw ?? '').replace(/\D/g, '');
  if (digits.length < 7) return null;
  return digits.slice(-9);
}
